import { Link } from "react-router-dom";
import { DISCIPLINE_THEME, BTN_FX, tpl, lessonStatus, resumeTarget } from "@/lib/academyTheme";
import { useT } from "@/i18n/LanguageContext";
import { LessonVirtueSeal } from "@/components/academy/LessonVirtueSeal";

const lessonUrl = (base, l) => `${base}/phase/${l.phaseSlug}/lesson/${l.slug}`;

/* Top navigation strip on every lesson: previous | position | next. */
export function LessonTopNav({ discipline, phase, lesson, prev, next, total, locTitle }) {
  const { t } = useT();
  const th = DISCIPLINE_THEME[discipline];
  return (
    <nav aria-label={t("academy.nav.lessonNavLabel")} data-testid="lesson-top-nav"
      className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-10 border border-white/10 bg-white/[0.02] p-3">
      {prev ? (
        <Link to={lessonUrl(th.base, prev)} data-testid="lesson-top-prev"
          className={`flex items-center gap-2 min-h-[44px] px-3 text-[11px] text-bone/70 hover:text-white focus:outline-none focus-visible:ring-2 ${th.ring}`}>
          <span aria-hidden>←</span>
          <span className="truncate">{t("academy.nav.prevLesson")}: {locTitle(prev.title)}</span>
        </Link>
      ) : (
        <Link to={th.base} data-testid="lesson-top-back-course"
          className={`flex items-center gap-2 min-h-[44px] px-3 text-[11px] text-bone/70 hover:text-white focus:outline-none focus-visible:ring-2 ${th.ring}`}>
          <span aria-hidden>←</span>{t("academy.nav.backToCourse")}
        </Link>
      )}
      <span className="flex items-center justify-center min-h-[44px] text-[10px] tracking-[0.2em] uppercase" style={{ color: th.accent }}>
        {tpl(t("academy.nav.position"), { phase: phase.order, lesson: lesson.order, total })}
      </span>
      {next ? (
        <Link to={lessonUrl(th.base, next)} data-testid="lesson-top-next"
          className={`flex items-center justify-end gap-2 min-h-[44px] px-3 text-[11px] text-bone/70 hover:text-white text-right focus:outline-none focus-visible:ring-2 ${th.ring}`}>
          <span className="truncate">{t("academy.nav.nextLesson")}: {locTitle(next.title)}</span>
          <span aria-hidden>→</span>
        </Link>
      ) : (
        <Link to={`${th.base}/${discipline === "logic" ? "chamber-of-judgment" : "chamber"}`} data-testid="lesson-top-chamber"
          className="flex items-center justify-end gap-2 min-h-[44px] px-3 text-[11px] text-gold hover:underline">
          {t("academy.nav.continueChamber")} <span aria-hidden>→</span>
        </Link>
      )}
    </nav>
  );
}

/* Bottom navigation: previous | back to phase | next / complete phase / chamber. */
export function LessonBottomNav({ discipline, phase, lesson, prev, next, locTitle }) {
  const { t } = useT();
  const th = DISCIPLINE_THEME[discipline];
  const chamberPath = `${th.base}/${discipline === "logic" ? "chamber-of-judgment" : "chamber"}`;
  const lastOfPhase = next && next.phaseSlug !== lesson.phaseSlug;
  return (
    <>
      <LessonVirtueSeal />
      <footer data-testid="lesson-bottom-nav" className="mt-4 pt-8 border-t border-white/10 space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {prev ? (
          <Link to={lessonUrl(th.base, prev)} data-testid="lesson-bottom-prev"
            className={`flex items-center gap-2 min-h-[52px] px-4 border border-white/15 text-[11px] text-bone/75 hover:text-white hover:border-white/35 focus:outline-none focus-visible:ring-2 ${th.ring}`}>
            <span aria-hidden>←</span>
            <span className="truncate">{tpl(t("academy.nav.lessonN"), { n: prev.order })}: {locTitle(prev.title)}</span>
          </Link>
        ) : (
          <Link to={th.base} data-testid="lesson-bottom-back-course"
            className="flex items-center gap-2 min-h-[52px] px-4 border border-white/15 text-[11px] text-bone/75 hover:text-white">
            <span aria-hidden>←</span>{t("academy.nav.backToCourse")}
          </Link>
        )}
        <Link to={`${th.base}/phase/${lesson.phaseSlug}`} data-testid="lesson-bottom-phase"
          className={`flex items-center justify-center min-h-[52px] px-4 border border-white/15 text-[11px] text-bone/75 hover:text-white focus:outline-none focus-visible:ring-2 ${th.ring}`}>
          {t("academy.nav.backToPhase")}
        </Link>
        {next ? (
          <Link to={lastOfPhase ? `${th.base}/phase/${next.phaseSlug}` : lessonUrl(th.base, next)} data-testid="lesson-bottom-next"
            className={`flex items-center justify-center gap-2 min-h-[52px] px-4 text-[11px] font-semibold tracking-[0.14em] uppercase ${th.btn} hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 ${th.ring}`}>
            {lastOfPhase
              ? tpl(t("academy.nav.completePhase"), { n: phase.order })
              : <>{tpl(t("academy.nav.lessonN"), { n: next.order })}: <span className="truncate max-w-[16ch]">{locTitle(next.title)}</span></>} <span aria-hidden>→</span>
          </Link>
        ) : (
          <Link to={chamberPath} data-testid="lesson-bottom-chamber"
            className={`flex items-center justify-center gap-2 min-h-[52px] px-4 text-[11px] font-semibold tracking-[0.14em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
            {t("academy.nav.continueChamber")} <span aria-hidden>→</span>
          </Link>
        )}
      </div>
    </footer>
    </>
  );
}

/* Prominent resume button for course overviews. */
export function ResumeButton({ discipline, course, records, chamberDone = false }) {
  const { t } = useT();
  const th = DISCIPLINE_THEME[discipline];
  if (course?.access === "preview") {
    return (
      <Link to="/my-stoicos" data-testid={`${discipline}-trial-cta`}
        className={`inline-flex items-center justify-center gap-2 min-h-[52px] px-8 text-xs font-semibold tracking-[0.24em] uppercase ${th.btn} hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ink ${th.ring} shadow-[0_0_24px_rgba(255,255,255,0.06)]`}>
        {t("trial.cta")} <span aria-hidden>→</span>
      </Link>
    );
  }
  const target = resumeTarget(course, records);
  const chamberPath = `${th.base}/${discipline === "logic" ? "chamber-of-judgment" : "chamber"}`;
  const pathPath = `${th.base}/${discipline === "logic" ? "progress" : "path"}`;
  const to = target ? lessonUrl(th.base, target) : chamberDone ? pathPath : chamberPath;
  const label = records.length === 0
    ? t("academy.nav.openCourseFirst")
    : target ? t("academy.nav.resume") : chamberDone ? t("academy.nav.viewPath") : t("academy.nav.continueChamber");
  return (
    <Link to={to} data-testid={`${discipline}-resume-button`}
      className={`inline-flex items-center justify-center gap-2 min-h-[52px] px-8 text-xs font-semibold tracking-[0.24em] uppercase ${th.btn} hover:brightness-125 active:brightness-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-ink ${th.ring} shadow-[0_0_24px_rgba(255,255,255,0.06)]`}>
      {label} <span aria-hidden>→</span>
    </Link>
  );
}

/* Not started / In progress / Completed badge. */
export function StatusBadge({ rec, accent = "#7fb69a" }) {
  const { t } = useT();
  const st = lessonStatus(rec);
  const map = { notStarted: "text-bone/40 border-white/15", inProgress: "border-current", completed: "text-gold border-gold/50" };
  return (
    <span data-testid="lesson-status-badge"
      className={`text-[10px] tracking-[0.18em] uppercase whitespace-nowrap border px-2 py-1 ${map[st]}`}
      style={st === "inProgress" ? { color: accent } : undefined}>
      {t(`academy.nav.status.${st}`)}
    </span>
  );
}

export function PhaseProgress({ phase, records, accent }) {
  const { t } = useT();
  const done = phase.lessons.filter((l) => lessonStatus(records.find((r) => r.lessonSlug === l.slug)) === "completed").length;
  return (
    <span className="text-[11px]" style={{ color: accent }} data-testid="phase-progress">
      {tpl(t("academy.nav.lessonsCompleted"), { x: done, y: phase.lessons.length })}
    </span>
  );
}

/* Completion panel shown after saving a record/exercise from a lesson context. */
export function CompletionPanel({ discipline, returnCtx, locTitle }) {
  const { t } = useT();
  const th = DISCIPLINE_THEME[discipline];
  if (!returnCtx) return null;
  const { lesson, prev, next, phaseSlug } = returnCtx;
  const pathPath = `${th.base}/${discipline === "logic" ? "progress" : "path"}`;
  const chamberPath = `${th.base}/${discipline === "logic" ? "chamber-of-judgment" : "chamber"}`;
  return (
    <section data-testid="completion-panel" className={`mt-8 p-6 ${th.frame} bg-white/[0.02] space-y-4`} aria-live="polite">
      <p className="font-serif text-xl text-bone">{t("academy.nav.reflectionSaved")}</p>
      <p className="text-[11px] tracking-[0.22em] uppercase" style={{ color: th.accent }}>{t("academy.nav.lessonCompleted")}</p>
      <div className="flex flex-wrap gap-3">
        {prev && (
          <Link to={lessonUrl(th.base, prev)} className="min-h-[44px] inline-flex items-center px-4 border border-white/15 text-[11px] text-bone/75 hover:text-white" data-testid="completion-prev">
            ← {t("academy.nav.prevLesson")}
          </Link>
        )}
        <Link to={lessonUrl(th.base, lesson)} className="min-h-[44px] inline-flex items-center px-4 border border-white/15 text-[11px] text-bone/75 hover:text-white" data-testid="completion-back-lesson">
          {t("academy.nav.backToLesson")} {lesson.order}
        </Link>
        {next ? (
          <Link to={lessonUrl(th.base, next)} data-testid="completion-next"
            className={`min-h-[44px] inline-flex items-center gap-2 px-5 text-[11px] font-semibold tracking-[0.14em] uppercase ${th.btn} hover:brightness-125`}>
            {tpl(t("academy.nav.continueToLesson"), { n: next.order })}: {locTitle(next.title)} →
          </Link>
        ) : (
          <Link to={chamberPath} data-testid="completion-chamber"
            className={`min-h-[44px] inline-flex items-center gap-2 px-5 text-[11px] font-semibold tracking-[0.14em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
            {t("academy.nav.continueChamber")} →
          </Link>
        )}
        <Link to={`${th.base}/phase/${phaseSlug}`} className="min-h-[44px] inline-flex items-center px-4 text-[11px] text-bone/60 hover:text-white underline-offset-4 hover:underline" data-testid="completion-phase">
          {t("academy.nav.viewPhase")}
        </Link>
        <Link to={pathPath} className="min-h-[44px] inline-flex items-center px-4 text-[11px] text-bone/60 hover:text-white underline-offset-4 hover:underline" data-testid="completion-progress">
          {t("academy.nav.viewProgress")}
        </Link>
        <Link to="/academy/my-work" className="min-h-[44px] inline-flex items-center px-4 text-[11px] text-bone/60 hover:text-white underline-offset-4 hover:underline" data-testid="completion-mywork">
          {t("academy.nav.myWork")}
        </Link>
      </div>
    </section>
  );
}

/* Back-to-lesson bar at the top of records/exercises opened from a lesson. */
export function ExerciseReturnBar({ discipline, returnCtx, locTitle }) {
  const { t } = useT();
  const th = DISCIPLINE_THEME[discipline];
  if (!returnCtx) return null;
  return (
    <Link to={lessonUrl(th.base, returnCtx.lesson)} data-testid="exercise-return-bar"
      className={`inline-flex items-center gap-2 min-h-[44px] px-4 mb-6 border text-[11px] text-bone/80 hover:text-white ${th.frame}`}>
      <span aria-hidden>←</span>
      {t("academy.nav.backToLesson")} {returnCtx.lesson.order}: {locTitle(returnCtx.lesson.title)}
    </Link>
  );
}
